import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {OrganizzazioniService} from './organizzazioni.service';
import {BehaviorSubject, catchError, firstValueFrom, of} from 'rxjs';
import {SearchResult} from '../_models/SearchResult';
import {OrdineVendita} from '../_models/OrdineVendita';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {SaveMessage} from '../_helpers/save-message';

export interface OrdiniVenditaFilterRequest {
  orgId: number;
  search?: string;
  zona?: string;
  startDate?: Date;
  endDate?: Date;
  page: number;
  count: number;
  sortField: string;
  sortAsc: boolean;
  attivitaId?: number | null;
}

@Injectable({providedIn: 'root'})
export class SaleOrdiniService {
  // Stato filtri persistito nella sessione (survives navigation)
  filtriAttivi: Partial<OrdiniVenditaFilterRequest> = {};

  // SignalR — OrdineVendita real-time updates
  public lastOrdineVenditaUpdated: BehaviorSubject<OrdineVendita> = new BehaviorSubject<OrdineVendita>(null);
  public lastOrdineVenditaCreated: BehaviorSubject<OrdineVendita> = new BehaviorSubject<OrdineVendita>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage
  ) {}

  private get baseUrl(): string {
    return `${environment.localApiUrl}sale/`;
  }

  private get orgId(): number {
    return this.organizzazioniService.organizzazioneId ?? 0;
  }

  public startConnection() {
    // Questo hub usa la negoziazione standard (nessuna opzione WebSocket-only).
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}sale/hubs/ordini`, this.hubState, {});

    this.hubConnection.on('SendUpdated', (ordine: OrdineVendita) => {
      if (ordine.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        if (ordine.hasErrors) {
          // Errori di validazione/processing dal worker (es. campi utente non conformi, feature 030).
          this.saveMessage.messageError(ordine.errorMessage ?? 'Errore nel salvataggio dell\'ordine.');
        }
        this.lastOrdineVenditaUpdated.next(ordine);
      }
    });

    // Il worker invia SendCreated anche dopo retry esauriti (payload con hasErrors).
    this.hubConnection.on('SendCreated', (ordine: OrdineVendita) => {
      if (ordine.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        if (ordine.hasErrors) {
          this.saveMessage.messageError(ordine.errorMessage ?? 'Errore nella creazione dell\'ordine.');
        }
        this.lastOrdineVenditaCreated.next(ordine);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  getOrdiniVendita(request: OrdiniVenditaFilterRequest): Promise<SearchResult<OrdineVendita>> {
    const emptyResult: SearchResult<OrdineVendita> = {result: [], totalCount: 0, hasErrors: true};
    return firstValueFrom(
      this.http.post<SearchResult<OrdineVendita>>(`${this.baseUrl}Ordini/OrdiniVenditaFiltered?orgId=${request.orgId}`, {orgId: request.orgId, ...request})
        .pipe(catchError(() => of(emptyResult)))
    );
  }

  async getZoneDisponibili(orgId: number): Promise<string[]> {
    return firstValueFrom(
      this.http.get<string[]>(`${this.baseUrl}Ordini/ZoneDistinte`, {params: {orgId}})
    );
  }

  async getOrdineVenditaById(idOrdine: number, idRiga: number): Promise<OrdineVendita> {
    return firstValueFrom(
      this.http.get<OrdineVendita>(`${this.baseUrl}Ordini/OrdineVenditaFromId`, {
        params: {organizzazioneId: this.orgId, idOrdine, idRiga}
      })
    );
  }

  async updateOrdineVendita(ordine: OrdineVendita): Promise<OrdineVendita> {
    return firstValueFrom(
      this.http.post<OrdineVendita>(`${this.baseUrl}Ordini/UpdateOrdineVendita`, ordine)
        .pipe(catchError(() => of(ordine)))
    );
  }
}
